import type { BadStatusError } from '@confluence/network';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';

import type { FetchOptions } from './fetchResponse';
import { useFetchData } from './useFetchData';
import type { TransformErrorType } from './handleError';
import { handleError } from './handleError';
import { useDeepCompareEffect } from './useDeepCompareEffect';
import { useExperimentalQueryCache } from './useExperimentalQueryCache';
import { useExperimentalQueryResults } from './useExperimentalQueryResults';

// Keeps track of in-flight requests to avoid duplicate requests for the same query + variables + fetchPolicy
const inFlightRequests = new Set<string>();

export type QueryResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: Error | undefined;
};

const initialResult = {
	data: undefined,
	loading: false,
	error: undefined,
};

export type FetchPolicy = 'cache-first' | 'network-only';

type UseExperimentalQueryParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	variables: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void;
	onError?: (error: Error | BadStatusError) => void;
	transformError?: TransformErrorType;
	skip?: boolean;
	fetchPolicy?: FetchPolicy;
};

export function useExperimentalQuery<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	variables,
	credentials,
	parseResult,
	onCompleted,
	onError,
	transformError,
	skip = false,
	fetchPolicy = 'cache-first',
}: UseExperimentalQueryParams<TData, TVariables, TResponse>): QueryResult<TData> {
	const { fetchData } = useFetchData<TData, TVariables, TResponse>({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
		parseResult,
	});

	const key = JSON.stringify({ url, variables });

	return useExperimentalQueryBase<TData, TVariables>({
		key,
		fetchData,
		variables,
		onCompleted,
		onError,
		transformError,
		skip,
		fetchPolicy,
	});
}

export type UseExperimentalQueryBaseParams<TData, TVariables> = {
	key: string;
	fetchData: () => TData | Promise<TData>;
	variables?: TVariables;
	onCompleted?: (data: TData) => void;
	onError?: (error: Error | BadStatusError) => void;
	transformError?: TransformErrorType;
	skip?: boolean;
	fetchPolicy?: FetchPolicy;
};

export function useExperimentalQueryBase<TData, TVariables>({
	key,
	fetchData,
	variables,
	onCompleted,
	onError,
	transformError,
	skip = false,
	fetchPolicy = 'cache-first',
}: UseExperimentalQueryBaseParams<TData, TVariables>): QueryResult<TData> {
	// Sweet state to sync same query calls across different components
	const [result, { setResult }] = useExperimentalQueryResults<TData>();
	// Cache for storing data from query calls
	const [cacheState, { setCacheEntry }] = useExperimentalQueryCache<TData>();

	const keyWithFetchPolicy = JSON.stringify({ key, fetchPolicy });

	const executeQuery = useStableCallback(async () => {
		inFlightRequests.add(keyWithFetchPolicy);

		setResult(key, { data: undefined, loading: true, error: undefined });

		try {
			let data;
			if (fetchPolicy === 'network-only') {
				data = await fetchData();
			} else {
				// Default cache-first fetch policy
				if (cacheState[key]) {
					data = cacheState[key];
				} else {
					data = await fetchData();
				}
			}

			// Store the data in cache
			if (!cacheState[key]) {
				setCacheEntry(key, data);
			}

			setResult(key, { data, loading: false, error: undefined });
			onCompleted?.(data);
		} catch (error) {
			const finalError = await handleError({ error, transformError, onError });
			setResult(key, { data: undefined, loading: false, error: finalError });
		} finally {
			inFlightRequests.delete(keyWithFetchPolicy);
		}
	});

	useDeepCompareEffect(() => {
		if (!skip && !inFlightRequests.has(keyWithFetchPolicy)) {
			void executeQuery();
		}
	}, [executeQuery, skip, variables]);

	return result[key] || initialResult;
}
